import React, { useState, useEffect } from 'react';
import {
  Document as DocxDocument,
  Packer,
  Paragraph,
  Table,
  TableRow,
  TableCell,
  TextRun,
  HeadingLevel,
  WidthType,
  AlignmentType,
} from 'docx';
import { Download, FileText, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { jobsService } from '../services/jobsService';
import ExcelMatrixExportModal from './ExcelMatrixExportModal';
import { MatrixData } from '../utils/excelMatrixExport';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

export interface Citation {
  docId?: string;
  docName?: string;
  page?: string;
  section?: string;
  quote?: string;
}

export interface RichText {
  text: string;
  citations?: Citation[];
}

export interface LedgerItem {
  key: string;
  component: string;
  standardCriteria: string;
  priorProvision?: RichText;
  provision?: RichText;
  /** N-way variant (home_quote_comparison) — one entry per quote, in submitted order. */
  provisions?: RichText[];
  /** Structured per-quote cell metadata (cyber_quote_comparison), parallel to `provisions`. */
  quoteCells?: QuoteCell[];
  grade: string;
  classification?: string;
  deltaClass?: string;
  materiality: string;
  note?: string;
}

export interface QuoteCell {
  quote: string;
  carrier?: string;
  grade: string;
  evidenceState: string;
  breadthClass: string;
}

export const EVIDENCE_STATE_STYLES: Record<string, string> = {
  SHOWN: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  'QUOTED-UNVERIFIED': 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  SILENT: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

export const BREADTH_CLASS_STYLES: Record<string, string> = {
  REFERENCE: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  BROADER: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  NARROWER: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  EQUIVALENT: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  'UNIQUE-TO': 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'ABSENT-FROM': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

/** Column header for quote column i: "Quote A · Travelers" from quoteCells when present, else "Quote i+1". */
export const quoteColumnLabel = (ledger: LedgerItem[], i: number): string => {
  const cell = ledger.find((l) => l.quoteCells?.[i])?.quoteCells?.[i];
  if (!cell) return `Quote ${i + 1}`;
  return cell.carrier ? `${cell.quote} · ${cell.carrier}` : cell.quote;
};

export interface NarrativeSection {
  id: string;
  title: string;
  /** Two-layer tools (owner benchmark, premium dev, CLM-1): which surface this section belongs to. */
  layer?: 'client' | 'annex';
  blocks: RichText[];
}

export interface ResearchScopeItem {
  item: string;
  volatility: string;
  datedNote: string;
  bothSideWording?: string;
}

interface SuggestedAnalysis {
  toolType: string;
  reason: string;
  triggeringFinding: string;
}

export interface DataTable {
  id: string;
  title: string;
  columns: string[];
  rows: string[][];
  notes?: string;
}

export interface PremiumBridgeStep {
  step: string;
  component: string;
  priorValue?: string;
  renewalValue?: string;
  delta?: string;
  driver: string;
  note?: string;
}

export interface ParseStatusItem {
  docId: string;
  docName: string;
  status: 'parsed' | 'attempted-failed' | 'novel-not-attempted';
  note?: string;
}

export interface SuiteEnvelope {
  responseMetadata: {
    workflowType: string;
    toolVersion: string;
    modelVersion: string;
    substratePin?: string;
    analysisDate: string;
    generatedAt?: string;
    routedVia?: string;
    routingRationale?: string;
    mode?: string;
    frame?: string;
  };
  inputsInventory: {
    received: string[];
    absent: string[];
    limitations?: RichText;
  };
  executiveSummary: {
    verdict: RichText;
    paragraphs: RichText[];
  };
  ledger?: LedgerItem[];
  narrativeSections: NarrativeSection[];
  researchScope: ResearchScopeItem[];
  reconciliation: {
    statement: RichText;
    countsTieOut: boolean;
  };
  suggestedAnalyses: SuggestedAnalysis[];
  dataTables?: DataTable[];
  premiumBridge?: PremiumBridgeStep[];
  parseStatus?: ParseStatusItem[];
  enhancements?: Record<string, SuiteEnvelope>;
}

interface SuiteReportRendererProps {
  data: SuiteEnvelope;
  onCitationClick: (citation: any) => void;
  hideCitations?: boolean;
  customerName?: string;
  reportId?: string;
  reportTitle?: string;
  isEnhancement?: boolean;
  /**
   * Opens the app's editable PDF export preview window (same one every other
   * report uses) with this envelope. When absent, "Export as PDF" falls back
   * to the direct server-side download.
   */
  onExportPdf?: (envelope: SuiteEnvelope) => void;
}

/** Extended per-suite as each suite ships; unknown types fall back to toolVersion/workflowType. */
export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  cyber_benchmark: 'Cyber Policy Benchmark',
  cyber_renewal_delta: 'Cyber Renewal Delta (YOY)',
  cyber_war_systemic: 'Cyber War & Systemic Exclusion Analyzer',
  cyber_ransomware_trap: 'Cyber Ransomware Trap Scanner',
  cyber_quote_comparison: 'Cyber Quote Comparison',
  do_benchmark: 'D&O Benchmark',
  do_renewal_delta: 'D&O Renewal Delta (YOY)',
  do_distress_scanner: 'D&O Financial Distress Scanner',
  do_emerging_exposure: 'D&O Emerging-Exposure Analyzer',
  home_owner_benchmark: 'HO Owner Benchmark',
  home_tenant_condo_benchmark: 'HO Tenant + Condo Benchmark',
  home_quote_comparison: 'HO Quote Comparison',
  home_renewal_delta: 'HO Renewal Delta (YOY)',
  home_dp_rental_benchmark: 'Dwelling Fire / Rental Benchmark',
  home_dp_rental_renewal_delta: 'Dwelling Fire / Rental Renewal Delta (YOY)',
  home_hnw_benchmark: 'HNW Homeowners Benchmark',
  pa_benchmark: 'Personal Auto Benchmark',
  pa_quote_comparison: 'Personal Auto Quote Comparison',
  pa_renewal_delta: 'Personal Auto Renewal Delta (YOY)',
  umb_benchmark: 'Personal Umbrella Benchmark',
  umb_quote_comparison: 'Personal Umbrella Quote Comparison',
  umb_renewal_delta: 'Personal Umbrella Renewal Delta (YOY)',
  rt_requirements_reviewer: 'Insurance Requirements Reviewer (RT-1R)',
  rt_conformance_alignment: 'Requirements Compliance Alignment (RT-3)',
  premdev_workers_comp: 'Premium Dev — Workers Comp',
  premdev_prof_liability: 'Premium Dev — Professional Liab + D&O',
  premdev_general_liability: 'Premium Dev — General Liability',
  premdev_property: 'Premium Dev — Property',
  premdev_package_bop: 'Premium Dev — Package / BOP',
  lossrun_fact_engine: 'Loss History Fact Engine',
  lossrun_mitigation_wc: 'Loss Mitigation / UW Presentation — WC',
  re_policy_extraction: 'RE Portfolio Coverage Extractor',
  re_risk_transfer: 'RE Contract Risk Transfer Analyzer',
  re_br_financials: "Builder's Risk Financials Extractor",
  re_zone_extractor: 'Procurement Zone-Value Extractor',
  re_portfolio_brief: 'RE Portfolio Board Brief',
  rt_compliance_verifier: 'Compliance Verifier (RT-2)',
  cl_gl_benchmark: 'GL Benchmark (CL-GL)',
  cl_property_benchmark: 'Commercial Property Benchmark',
  cl_commercial_auto_benchmark: 'Commercial Auto Benchmark',
  cl_inland_marine_benchmark: 'Inland Marine Benchmark',
  cl_excess_benchmark: 'Excess & Umbrella Benchmark',
  cl_package_benchmark: 'Commercial Package Benchmark',
  cl_bop_benchmark: 'BOP Benchmark',
  cl_nytpao_flow: 'NY Action-Over Flow Analysis',
  cl_package_quote_comparison: 'Package Quote Comparison',
  cl_bop_quote_comparison: 'BOP Quote Comparison',
  cl_xarch_quote_comparison: 'Cross-Architecture Quote Comparison (PKG↔BOP)',
  cl_package_renewal_delta: 'Package Renewal Delta (YOY)',
  cl_bop_renewal_delta: 'BOP Renewal Delta (YOY)',
  ca_coverage_determination: 'Coverage Determination (CA-1)',
  ca_portfolio_determination: 'Portfolio Coverage Determination (CA-2)',
  ca_carrier_position_audit: 'Carrier Position Audit (CA-3)',
  ntr_first_party: 'Notice Trigger Register — First-Party & Specialty',
  ntr_property_liability: 'Notice Trigger Register — Property & Liability',
  ntr_mgmt_professional: 'Notice Trigger Register — Management & Professional',
};

export const GRADE_STYLES: Record<string, string> = {
  MEETS: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  PARTIAL: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  GAP: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  TRAP: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  TRAP_UNVERIFIED: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border border-red-300 dark:border-red-700',
  NA: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  // CA-1's Block 1 three-state map (CA-0's own vocabulary, never a second scale)
  GRANTED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  EXCLUDED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  CONTESTED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  // RT-2's verdict grammar
  MET: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  NOT_MET: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  EVIDENCE_INSUFFICIENT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  NOT_APPLICABLE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  // NTR's recognition-row states
  FIRED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  DORMANT: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  ABSENCE_FINDING: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};

export const DELTA_STYLES: Record<string, string> = {
  DEGRADE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  IMPROVE: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  NEUTRAL_RESTATE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  NEW: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  REMOVED: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  // D&O DO-T3 v0.3 status labels (hold a ledger row outside the verdict; never a delta class)
  NO_CHANGE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  PASS_THROUGH: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  UNRESOLVED: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};

export const VOLATILITY_STYLES: Record<string, string> = {
  'VERY HIGH': 'bg-amber-200 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200',
  HIGH: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  MED: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  LOW: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export const PARSE_STATUS_STYLES: Record<string, string> = {
  parsed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  'attempted-failed': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  'novel-not-attempted': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const Chip: React.FC<{ label: string; className?: string }> = ({ label, className }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${className || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
    {label}
  </span>
);

const renderMarkdownBold = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

const RichTextView: React.FC<{
  value?: RichText;
  onCitationClick: (citation: any) => void;
  hideCitations?: boolean;
}> = ({ value, onCitationClick, hideCitations }) => {
  if (!value || !value.text) return <span className="text-gray-400 italic">—</span>;
  const citations = value.citations || [];
  return (
    <span>
      {renderMarkdownBold(value.text)}
      {!hideCitations && citations.length > 0 && (
        <span className="inline-flex items-center gap-1 ml-1 align-middle">
          {citations.map((c, i) => (
            <button
              key={i}
              type="button"
              className="inline-flex items-center text-[10px] bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
              title={`${c.docName || 'Document'}${c.page ? ` · p.${c.page}` : ''}${c.section ? ` · ${c.section}` : ''}`}
              onClick={() =>
                onCitationClick({
                  type: 'page',
                  page: c.page,
                  docId: c.docId,
                  documentId: c.docId,
                  sourceDocId: c.docId,
                  documentName: c.docName,
                  section: c.section,
                  text: c.quote,
                })
              }
            >
              📄
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

const SectionCard: React.FC<{ id?: string; title: string; children: React.ReactNode; className?: string }> = ({ id, title, children, className }) => (
  <div id={id} className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 ${className || ''}`}>
    <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">{title}</h3>
    {children}
  </div>
);

/** Serializes a DataTable to CSV and triggers a client-side download — honors Loss Run CLM-1's CSV-contract output without a server round-trip. */
const downloadDataTableCsv = (table: DataTable) => {
  const escapeCell = (cell: string) => {
    if (cell == null) return '';
    const needsQuote = /[",\n]/.test(cell);
    const escaped = cell.replace(/"/g, '""');
    return needsQuote ? `"${escaped}"` : escaped;
  };
  const lines = [table.columns.map(escapeCell).join(','), ...table.rows.map((row) => row.map(escapeCell).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${table.id || table.title || 'data'}.csv`.replace(/[^a-z0-9_.-]/gi, '_');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const DataTableView: React.FC<{ table: DataTable }> = ({ table }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{table.title}</h4>
      <button
        type="button"
        onClick={() => downloadDataTableCsv(table)}
        className="text-xs font-medium px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
      >
        Download CSV
      </button>
    </div>
    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
            {table.columns.map((c, i) => (
              <th key={i} className="py-2 px-3 whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
              {row.map((cell, j) => (
                <td key={j} className="py-1.5 px-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {table.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{table.notes}</p>}
  </div>
);

const PremiumBridgeView: React.FC<{ steps: PremiumBridgeStep[] }> = ({ steps }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
          <th className="py-2 pr-3">Step</th>
          <th className="py-2 pr-3">Component</th>
          <th className="py-2 pr-3">Prior</th>
          <th className="py-2 pr-3">Renewal</th>
          <th className="py-2 pr-3">Δ</th>
          <th className="py-2 pr-3">Driver</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s, i) => (
          <tr key={i} className="border-b border-gray-100 dark:border-gray-800 align-top">
            <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{s.step}</td>
            <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{s.component}</td>
            <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">{s.priorValue || '—'}</td>
            <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">{s.renewalValue || '—'}</td>
            <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{s.delta || '—'}</td>
            <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">{s.driver}{s.note ? ` — ${s.note}` : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ParseStatusStrip: React.FC<{ items: ParseStatusItem[] }> = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((it, i) => (
      <div key={i} className="flex items-center gap-1.5 text-xs">
        <Chip label={it.status} className={PARSE_STATUS_STYLES[it.status]} />
        <span className="text-gray-600 dark:text-gray-400">{it.docName}{it.note ? ` — ${it.note}` : ''}</span>
      </div>
    ))}
  </div>
);

const NarrativeBlock: React.FC<{
  section: NarrativeSection;
  idPrefix: string;
  onCitationClick: (citation: any) => void;
  hideCitations?: boolean;
}> = ({ section, idPrefix, onCitationClick, hideCitations }) => (
  <SectionCard id={`${idPrefix}-narrative-${section.id}`} title={section.title}>
    <div className="space-y-2">
      {section.blocks.map((b, i) => (
        <p key={i} className="text-sm text-gray-700 dark:text-gray-300">
          <RichTextView value={b} onCitationClick={onCitationClick} hideCitations={hideCitations} />
        </p>
      ))}
    </div>
  </SectionCard>
);

const SuiteReportBody: React.FC<SuiteReportRendererProps> = ({ data, onCitationClick, hideCitations, isEnhancement }) => {
  const meta = data.responseMetadata || ({} as any);
  const toolName = TOOL_DISPLAY_NAMES[meta.workflowType] || meta.toolVersion || meta.workflowType;
  // Unique id prefix per body so a base report + N merged enhancements never
  // collide on the same page — also what WorkflowReport.tsx's Table of
  // Contents section-id builder targets.
  const idPrefix = isEnhancement ? `suite-enh-${meta.workflowType}` : 'suite';

  const ledger = data.ledger || [];
  const hasProvisionsColumns = ledger.some((l) => l.provisions && l.provisions.length > 0);
  const quoteColumnCount = hasProvisionsColumns ? Math.max(...ledger.map((l) => l.provisions?.length || 0)) : 0;
  // N-way quote comparisons compare quotes to each other now — prior/delta
  // columns don't apply there even if a row carries a stray value.
  const showPriorColumn = !hasProvisionsColumns && ledger.some((l) => l.priorProvision);
  const showDeltaColumn = !hasProvisionsColumns && ledger.some((l) => l.deltaClass);

  // Two-layer tools (owner benchmark, premium dev, CLM-1) tag sections with
  // `layer`; single-layer tools leave it unset and everything renders inline.
  const clientSections = data.narrativeSections.filter((s) => !s.layer || s.layer === 'client');
  const annexSections = data.narrativeSections.filter((s) => s.layer === 'annex');

  return (
    <div className="space-y-6">
      {/* Report header */}
      <div
        id={`${idPrefix}-header`}
        className={`rounded-lg p-4 border ${isEnhancement ? 'bg-violet-50 dark:bg-violet-900/10 border-violet-200 dark:border-violet-800' : 'bg-primary/5 border-primary/20'}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{toolName}</h2>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            {meta.frame && <Chip label={`Frame: ${meta.frame}`} className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300" />}
            {meta.mode && <Chip label={meta.mode} className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300" />}
            <span>Analysis date: {meta.analysisDate}</span>
          </div>
        </div>
        {meta.routedVia && (
          <div className="mt-2 text-sm text-violet-800 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/30 rounded px-3 py-2">
            Automatic routing chose <strong>{toolName}</strong>{meta.routingRationale ? ` — ${meta.routingRationale}` : ''}
          </div>
        )}
      </div>

      {/* Inputs inventory */}
      <SectionCard id={`${idPrefix}-inputs`} title="Inputs">
        <div className="flex flex-wrap gap-4 text-sm">
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Received</span>
            <div className="flex flex-wrap gap-1">
              {(data.inputsInventory?.received || []).map((r, i) => (
                <Chip key={i} label={r} className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" />
              ))}
              {(data.inputsInventory?.received || []).length === 0 && <span className="text-gray-400 italic text-xs">none</span>}
            </div>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Absent</span>
            <div className="flex flex-wrap gap-1">
              {(data.inputsInventory?.absent || []).map((a, i) => (
                <Chip key={i} label={a} className="bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400" />
              ))}
              {(data.inputsInventory?.absent || []).length === 0 && <span className="text-gray-400 italic text-xs">none</span>}
            </div>
          </div>
        </div>
        {data.inputsInventory?.limitations?.text && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            <RichTextView value={data.inputsInventory.limitations} onCitationClick={onCitationClick} hideCitations={hideCitations} />
          </p>
        )}
      </SectionCard>

      {/* Executive summary */}
      <SectionCard id={`${idPrefix}-executive-summary`} title="Executive Summary">
        <p className="text-base font-medium text-gray-900 dark:text-white mb-3">
          <RichTextView value={data.executiveSummary?.verdict} onCitationClick={onCitationClick} hideCitations={hideCitations} />
        </p>
        <div className="space-y-2">
          {(data.executiveSummary?.paragraphs || []).map((p, i) => (
            <p key={i} className="text-sm text-gray-700 dark:text-gray-300">
              <RichTextView value={p} onCitationClick={onCitationClick} hideCitations={hideCitations} />
            </p>
          ))}
        </div>
      </SectionCard>

      {/* Ledger — grading-style tools only (benchmark/renewal-delta); omitted by premium/loss-run/RE tools */}
      {ledger.length > 0 && (
        <SectionCard id={`${idPrefix}-ledger`} title="Ledger">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-3">Component</th>
                  <th className="py-2 pr-3">Standard</th>
                  {showPriorColumn && <th className="py-2 pr-3">Prior</th>}
                  {hasProvisionsColumns
                    ? Array.from({ length: quoteColumnCount }, (_, i) => (
                        <th key={i} className="py-2 pr-3">{quoteColumnLabel(ledger, i)}</th>
                      ))
                    : <th className="py-2 pr-3">Provision</th>}
                  <th className="py-2 pr-3">Grade</th>
                  {showDeltaColumn && <th className="py-2 pr-3">Delta</th>}
                  <th className="py-2 pr-3">Materiality</th>
                  <th className="py-2 pr-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row) => (
                  <tr key={row.key} className="border-b border-gray-100 dark:border-gray-800 align-top">
                    <td className={`py-2 pr-3 font-medium text-gray-900 dark:text-white ${hasProvisionsColumns ? 'min-w-[9rem] max-w-[14rem]' : 'whitespace-nowrap'}`}>
                      {row.key} · {row.component}
                    </td>
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-400 max-w-xs">{row.standardCriteria}</td>
                    {showPriorColumn && (
                      <td className="py-2 pr-3 max-w-xs">
                        {row.priorProvision ? <RichTextView value={row.priorProvision} onCitationClick={onCitationClick} hideCitations={hideCitations} /> : <span className="text-gray-400 italic">—</span>}
                      </td>
                    )}
                    {hasProvisionsColumns
                      ? Array.from({ length: quoteColumnCount }, (_, i) => (
                          <td key={i} className="py-2 pr-3 min-w-[16rem] max-w-sm">
                            {row.quoteCells?.[i] && (
                              <div className="flex flex-wrap gap-1 mb-1">
                                <Chip label={row.quoteCells[i].grade} className={GRADE_STYLES[row.quoteCells[i].grade]} />
                                <Chip label={row.quoteCells[i].evidenceState} className={EVIDENCE_STATE_STYLES[row.quoteCells[i].evidenceState]} />
                                <Chip label={row.quoteCells[i].breadthClass} className={BREADTH_CLASS_STYLES[row.quoteCells[i].breadthClass]} />
                              </div>
                            )}
                            {row.provisions?.[i] ? <RichTextView value={row.provisions[i]} onCitationClick={onCitationClick} hideCitations={hideCitations} /> : <span className="text-gray-400 italic">—</span>}
                          </td>
                        ))
                      : (
                        <td className="py-2 pr-3 max-w-sm">
                          <RichTextView value={row.provision} onCitationClick={onCitationClick} hideCitations={hideCitations} />
                          {row.classification && (
                            <div className="mt-1"><Chip label={row.classification} className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300" /></div>
                          )}
                        </td>
                      )}
                    <td className="py-2 pr-3"><Chip label={row.grade} className={GRADE_STYLES[row.grade]} /></td>
                    {showDeltaColumn && (
                      <td className="py-2 pr-3">{row.deltaClass ? <Chip label={row.deltaClass} className={DELTA_STYLES[row.deltaClass]} /> : null}</td>
                    )}
                    <td className="py-2 pr-3"><Chip label={row.materiality} /></td>
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-400 max-w-xs">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Premium bridge — Premium Dev's ordered walk from prior to renewal premium */}
      {(data.premiumBridge || []).length > 0 && (
        <SectionCard id={`${idPrefix}-premium-bridge`} title="Premium Bridge">
          <PremiumBridgeView steps={data.premiumBridge as PremiumBridgeStep[]} />
        </SectionCard>
      )}

      {/* Parse status — Real Estate's per-document parsed/attempted-failed/novel declaration */}
      {(data.parseStatus || []).length > 0 && (
        <SectionCard id={`${idPrefix}-parse-status`} title="Parse Coverage">
          <ParseStatusStrip items={data.parseStatus as ParseStatusItem[]} />
        </SectionCard>
      )}

      {/* Data tables — claim CSVs (Loss Run), extraction exhibits (RE), classification/state schedules (Premium Dev) */}
      {(data.dataTables || []).length > 0 && (
        <div id={`${idPrefix}-data-tables`} className="space-y-4">
          {(data.dataTables as DataTable[]).map((t) => (
            <SectionCard key={t.id} title="">
              <DataTableView table={t} />
            </SectionCard>
          ))}
        </div>
      )}

      {/* Narrative sections — client-facing surface */}
      {clientSections.map((section) => (
        <NarrativeBlock key={section.id} section={section} idPrefix={idPrefix} onCitationClick={onCitationClick} hideCitations={hideCitations} />
      ))}

      {/* Consultant technical annex — two-layer tools only, collapsed by default */}
      {annexSections.length > 0 && (
        <details id={`${idPrefix}-annex`} className="bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Consultant Technical Annex
          </summary>
          <div className="mt-4 space-y-4">
            {annexSections.map((section) => (
              <NarrativeBlock key={section.id} section={section} idPrefix={idPrefix} onCitationClick={onCitationClick} hideCitations={hideCitations} />
            ))}
          </div>
        </details>
      )}

      {/* Research scope */}
      {(data.researchScope || []).length > 0 && (
        <SectionCard id={`${idPrefix}-research-scope`} title="Research Scope — Verify Before Placement" className="border-amber-200 dark:border-amber-800">
          <ul className="space-y-3">
            {data.researchScope.map((item, i) => (
              <li key={i} className="bg-amber-50 dark:bg-amber-900/10 rounded p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Chip label={item.volatility} className={VOLATILITY_STYLES[item.volatility]} />
                  <span className="font-medium text-sm text-gray-900 dark:text-white">{item.item}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{item.datedNote}</p>
                {item.bothSideWording && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{item.bothSideWording}</p>}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {/* Reconciliation */}
      <SectionCard id={`${idPrefix}-reconciliation`} title="Reconciliation">
        <div className="flex items-center gap-2">
          <Chip
            label={data.reconciliation?.countsTieOut ? 'Tie-out confirmed' : 'Tie-out NOT confirmed'}
            className={data.reconciliation?.countsTieOut ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'}
          />
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
          <RichTextView value={data.reconciliation?.statement} onCitationClick={onCitationClick} hideCitations={hideCitations} />
        </p>
      </SectionCard>
    </div>
  );
};

// ============================================================================
// Export — dedicated structured generators (never a markdown round-trip).
// PDF renders server-side (suiteEnvelopePdf.ts, same envelope shape); DOCX
// builds client-side here, mirroring the same section order.
// ============================================================================

const stripInlineCitesText = (s?: string): string => (s || '').replace(/\[CITE:\s*"[^"]+"\]/g, '').replace(/\s{2,}/g, ' ').trim();
const richPlain = (v?: RichText): string => (v?.text ? stripInlineCitesText(v.text) : '—');

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const safeFilename = (s: string, ext: string): string => `${(s || 'report').replace(/[^a-z0-9\-_. ]/gi, '_')}.${ext}`;

const addEnvelopeSectionsToDocx = (children: any[], data: SuiteEnvelope, toolName: string, isEnhancement: boolean) => {
  const meta = data.responseMetadata || ({} as any);

  children.push(new Paragraph({ text: toolName, heading: isEnhancement ? HeadingLevel.HEADING_1 : HeadingLevel.TITLE }));
  children.push(
    new Paragraph({
      children: [
        new TextRun({ text: `Analysis date: ${meta.analysisDate || '—'}`, italics: true, size: 18 }),
        ...(meta.frame ? [new TextRun({ text: `   Frame: ${meta.frame}`, italics: true, size: 18 })] : []),
        ...(meta.mode ? [new TextRun({ text: `   Mode: ${meta.mode}`, italics: true, size: 18 })] : []),
      ],
    })
  );
  if (meta.routedVia) {
    children.push(
      new Paragraph({ children: [new TextRun({ text: `Automatic routing chose ${toolName}${meta.routingRationale ? ` — ${meta.routingRationale}` : ''}`, italics: true })] })
    );
  }

  const heading = (text: string) => children.push(new Paragraph({ text, heading: HeadingLevel.HEADING_2 }));
  const kvTable = (rows: string[][], headerRow?: string[]) => {
    const allRows = headerRow ? [headerRow, ...rows] : rows;
    children.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: allRows.map(
          (r, i) =>
            new TableRow({
              tableHeader: headerRow ? i === 0 : false,
              children: r.map((cell) => new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: cell, bold: headerRow ? i === 0 : false })] })] } as any)),
            })
        ),
      })
    );
    children.push(new Paragraph({ text: '' }));
  };

  heading('Inputs');
  children.push(new Paragraph({ children: [new TextRun({ text: 'Received: ', bold: true }), new TextRun({ text: (data.inputsInventory?.received || []).join(', ') || 'none' })] }));
  children.push(new Paragraph({ children: [new TextRun({ text: 'Absent: ', bold: true }), new TextRun({ text: (data.inputsInventory?.absent || []).join(', ') || 'none' })] }));
  if (data.inputsInventory?.limitations?.text) children.push(new Paragraph({ text: richPlain(data.inputsInventory.limitations) }));
  children.push(new Paragraph({ text: '' }));

  heading('Executive Summary');
  children.push(new Paragraph({ children: [new TextRun({ text: richPlain(data.executiveSummary?.verdict), bold: true })] }));
  (data.executiveSummary?.paragraphs || []).forEach((p) => children.push(new Paragraph({ text: richPlain(p) })));
  children.push(new Paragraph({ text: '' }));

  const ledger = data.ledger || [];
  if (ledger.length > 0) {
    heading('Ledger');
    kvTable(
      ledger.map((row) => [
        `${row.key} · ${row.component}`,
        row.standardCriteria,
        row.provisions?.length ? row.provisions.map((p) => richPlain(p)).join(' | ') : richPlain(row.provision),
        row.grade,
        row.materiality,
        row.note || '',
      ]),
      ['Component', 'Standard', 'Provision', 'Grade', 'Materiality', 'Note']
    );
  }

  if (data.premiumBridge?.length) {
    heading('Premium Bridge');
    kvTable(
      data.premiumBridge.map((s) => [s.step, s.component, s.priorValue || '—', s.renewalValue || '—', s.delta || '—', `${s.driver}${s.note ? ` — ${s.note}` : ''}`]),
      ['Step', 'Component', 'Prior', 'Renewal', 'Δ', 'Driver']
    );
  }

  if (data.parseStatus?.length) {
    heading('Parse Coverage');
    data.parseStatus.forEach((it) => children.push(new Paragraph({ text: `${it.status} — ${it.docName}${it.note ? ` — ${it.note}` : ''}`, bullet: { level: 0 } })));
    children.push(new Paragraph({ text: '' }));
  }

  (data.dataTables || []).forEach((t) => {
    heading(t.title || 'Data Table');
    if (t.columns?.length) kvTable(t.rows || [], t.columns);
    if (t.notes) children.push(new Paragraph({ children: [new TextRun({ text: t.notes, italics: true })] }));
  });

  const narrative = data.narrativeSections || [];
  const clientSections = narrative.filter((s) => !s.layer || s.layer === 'client');
  const annexSections = narrative.filter((s) => s.layer === 'annex');
  clientSections.forEach((s) => {
    heading(s.title);
    s.blocks.forEach((b) => children.push(new Paragraph({ text: richPlain(b) })));
  });
  if (annexSections.length > 0) {
    heading('Consultant Technical Annex');
    annexSections.forEach((s) => {
      children.push(new Paragraph({ children: [new TextRun({ text: s.title, bold: true })] }));
      s.blocks.forEach((b) => children.push(new Paragraph({ text: richPlain(b) })));
    });
  }

  if (data.researchScope?.length) {
    heading('Research Scope — Verify Before Placement');
    data.researchScope.forEach((it) => {
      children.push(new Paragraph({ children: [new TextRun({ text: `[${it.volatility}] `, bold: true }), new TextRun({ text: it.item, bold: true })] }));
      children.push(new Paragraph({ text: it.datedNote }));
      if (it.bothSideWording) children.push(new Paragraph({ children: [new TextRun({ text: it.bothSideWording, italics: true })] }));
    });
    children.push(new Paragraph({ text: '' }));
  }

  heading('Reconciliation');
  children.push(new Paragraph({ children: [new TextRun({ text: data.reconciliation?.countsTieOut ? 'Tie-out confirmed' : 'Tie-out NOT confirmed', bold: true })] }));
  children.push(new Paragraph({ text: richPlain(data.reconciliation?.statement) }));
  children.push(new Paragraph({ text: '' }));
};

const buildSuiteEnvelopeDocx = async (data: SuiteEnvelope, customerName: string | undefined, title: string): Promise<Blob> => {
  const children: any[] = [];
  const baseWorkflowType = data.responseMetadata?.workflowType || '';
  const toolName = TOOL_DISPLAY_NAMES[baseWorkflowType] || data.responseMetadata?.toolVersion || baseWorkflowType;

  children.push(new Paragraph({ text: title, heading: HeadingLevel.TITLE, alignment: AlignmentType.LEFT }));
  if (customerName) children.push(new Paragraph({ children: [new TextRun({ text: customerName, bold: true })] }));
  children.push(new Paragraph({ text: '' }));

  addEnvelopeSectionsToDocx(children, data, toolName, false);

  Object.entries(data.enhancements || {}).forEach(([key, enhancementData]) => {
    const enhancementType = key.split(':')[0];
    const enhancementName = TOOL_DISPLAY_NAMES[enhancementType] || enhancementType;
    children.push(new Paragraph({ text: '' }));
    addEnvelopeSectionsToDocx(children, enhancementData, enhancementName, true);
  });

  const doc = new DocxDocument({ sections: [{ properties: {}, children }] });
  return Packer.toBlob(doc);
};

/** Builds one section's tabular content (ledger/premiumBridge/dataTables) as MatrixData sheets, prefixed so base-report and enhancement sheets stay distinguishable. */
const addEnvelopeMatrices = (matrices: MatrixData[], data: SuiteEnvelope, sheetPrefix: string) => {
  const ledger = data.ledger || [];
  if (ledger.length > 0) {
    matrices.push({
      title: sheetPrefix ? `${sheetPrefix} Ledger` : 'Ledger',
      headers: ['Key', 'Component', 'Standard', 'Provision', 'Grade', 'Materiality', 'Note'],
      rows: ledger.map((row) => [
        row.key,
        row.component,
        row.standardCriteria,
        row.provisions?.length ? row.provisions.map((p) => richPlain(p)).join(' | ') : richPlain(row.provision),
        row.grade,
        row.materiality,
        row.note || '',
      ]),
    });
  }

  if (data.premiumBridge?.length) {
    matrices.push({
      title: sheetPrefix ? `${sheetPrefix} Premium Bridge` : 'Premium Bridge',
      headers: ['Step', 'Component', 'Prior', 'Renewal', 'Delta', 'Driver'],
      rows: data.premiumBridge.map((s) => [s.step, s.component, s.priorValue || '', s.renewalValue || '', s.delta || '', `${s.driver}${s.note ? ` — ${s.note}` : ''}`]),
    });
  }

  (data.dataTables || []).forEach((t) => {
    if (!t.columns?.length) return;
    matrices.push({
      title: sheetPrefix ? `${sheetPrefix} ${t.title || 'Data'}` : t.title || 'Data',
      headers: t.columns,
      rows: t.rows || [],
    });
  });
};

const extractSuiteMatrices = (data: SuiteEnvelope): MatrixData[] => {
  const matrices: MatrixData[] = [];
  addEnvelopeMatrices(matrices, data, '');
  Object.entries(data.enhancements || {}).forEach(([key, enhancementData]) => {
    const enhancementType = key.split(':')[0];
    const enhancementName = TOOL_DISPLAY_NAMES[enhancementType] || enhancementType;
    addEnvelopeMatrices(matrices, enhancementData, enhancementName);
  });
  return matrices;
};

/** Full renderer: base report body + suggested-analyses cards + merged enhancements. Shared by every BETA suite (cyber, D&O, Home, Premium Dev, Loss Run, Real Estate). */
export const SuiteReportRenderer: React.FC<SuiteReportRendererProps> = (props) => {
  const { token } = useAuth();
  const [liveData, setLiveData] = useState<SuiteEnvelope>(props.data);
  const [enhancingTool, setEnhancingTool] = useState<string | null>(null);
  const [enhanceError, setEnhanceError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'pdf' | 'docx' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [showExcelExportModal, setShowExcelExportModal] = useState(false);

  useEffect(() => {
    setLiveData(props.data);
  }, [props.data]);

  const refreshReport = async () => {
    if (!props.reportId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/comparison-reports/${props.reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const report = await res.json();
      const parsed = typeof report.content === 'string' ? JSON.parse(report.content) : report.content;
      setLiveData(parsed);
    } catch (err) {
      console.error('SuiteReportRenderer: failed to refresh report after enhancement', err);
    }
  };

  // The one suite tool (v1) whose suggestion button compiles a NEW report
  // from every conforming producer report in the project, rather than
  // merging into the report that surfaced the suggestion — see
  // WorkflowConfig.compilesNewReport (RE Portfolio Brief).
  const COMPILE_TOOL_TYPES = new Set(['re_portfolio_brief']);

  const runEnhancement = async (toolType: string) => {
    if (!props.reportId || !token) return;
    setEnhancingTool(toolType);
    setEnhanceError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/workflows/reports/${props.reportId}/enhance`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ toolType }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to start enhancement' }));
        throw new Error(err.error || 'Failed to start enhancement');
      }
      const { jobId, compilesNewReport } = await res.json();

      // Poll the job to completion (reuses the standard jobs polling endpoint).
      let attempts = 0;
      const maxAttempts = 300; // 10 minutes at 2s
      while (attempts < maxAttempts) {
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const jobStatus = await jobsService.getJob(token, jobId);
        if (jobStatus.job.status === 'completed') {
          if (compilesNewReport || COMPILE_TOOL_TYPES.has(toolType)) {
            const newReportId = jobStatus.job.savedReportId;
            setEnhancingTool(null);
            if (newReportId) {
              window.location.href = `/workflow-report?reportId=${newReportId}`;
            }
            return;
          }
          await refreshReport();
          setEnhancingTool(null);
          return;
        }
        if (jobStatus.job.status === 'failed' || jobStatus.job.status === 'dead_letter') {
          throw new Error(jobStatus.job.lastError || 'Enhancement analysis failed');
        }
      }
      throw new Error('Enhancement timed out — check back shortly, it may still complete.');
    } catch (err: any) {
      console.error('SuiteReportRenderer: enhancement failed', err);
      setEnhanceError(err.message || 'Enhancement failed');
      setEnhancingTool(null);
    }
  };

  const reportTitle = props.reportTitle || TOOL_DISPLAY_NAMES[liveData.responseMetadata?.workflowType] || liveData.responseMetadata?.toolVersion || 'Report';
  const suiteMatrices = extractSuiteMatrices(liveData);

  const handleExportPdf = async () => {
    if (props.onExportPdf) {
      setExportMenuOpen(false);
      props.onExportPdf(liveData);
      return;
    }
    setExporting('pdf');
    setExportError(null);
    setExportMenuOpen(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/workflows/reports/suite-envelope-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ envelope: liveData, title: reportTitle, customerName: props.customerName }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`PDF export failed (${res.status}) ${errText.slice(0, 200)}`);
      }
      const result = await res.json();
      if (!result?.success || !result?.pdfBase64) throw new Error('PDF export returned no data');
      const byteArray = Uint8Array.from(atob(result.pdfBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      downloadBlob(blob, safeFilename(reportTitle, 'pdf'));
    } catch (err: any) {
      console.error('SuiteReportRenderer: PDF export failed', err);
      setExportError(err?.message || 'PDF export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleExportDocx = async () => {
    setExporting('docx');
    setExportError(null);
    setExportMenuOpen(false);
    try {
      const blob = await buildSuiteEnvelopeDocx(liveData, props.customerName, reportTitle);
      downloadBlob(blob, safeFilename(reportTitle, 'docx'));
    } catch (err: any) {
      console.error('SuiteReportRenderer: DOCX export failed', err);
      setExportError(err?.message || 'DOCX export failed');
    } finally {
      setExporting(null);
    }
  };

  const enhancements = liveData.enhancements || {};

  // Defensive dedupe by toolType: the model can legitimately raise the same
  // toolType from two different triggering findings in one run, and a merge
  // can independently carry a duplicate forward from the base + the newly
  // merged tool's own output. Keep the first occurrence of each toolType.
  const suggested = (() => {
    const seen = new Set<string>();
    return (liveData.suggestedAnalyses || []).filter((s) => {
      if (!s?.toolType || seen.has(s.toolType)) return false;
      seen.add(s.toolType);
      return true;
    });
  })();

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-end gap-2 relative">
        {exportError && <span className="text-xs text-red-600 dark:text-red-400">{exportError}</span>}
        <button
          type="button"
          onClick={() => setExportMenuOpen((v) => !v)}
          disabled={!!exporting}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Export report"
          aria-haspopup="menu"
          aria-expanded={exportMenuOpen}
        >
          <Download className="w-3.5 h-3.5" />
          <span>{exporting ? (exporting === 'pdf' ? 'Exporting PDF…' : 'Exporting Word…') : 'Export'}</span>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {exportMenuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
            <div role="menu" className="absolute right-0 top-9 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
              <button
                role="menuitem"
                onClick={handleExportPdf}
                disabled={!!exporting}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <Download className="w-4 h-4 text-red-600" />
                <span>Export as PDF</span>
              </button>
              <button
                role="menuitem"
                onClick={handleExportDocx}
                disabled={!!exporting}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <FileText className="w-4 h-4 text-blue-600" />
                <span>Export as Word</span>
              </button>
              {suiteMatrices.length > 0 && (
                <button
                  role="menuitem"
                  onClick={() => { setExportMenuOpen(false); setShowExcelExportModal(true); }}
                  disabled={!!exporting}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <FileSpreadsheet className="w-4 h-4 text-green-600" />
                  <span>Export tables (Excel)</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <ExcelMatrixExportModal
        isOpen={showExcelExportModal}
        onClose={() => setShowExcelExportModal(false)}
        matrices={suiteMatrices}
        clientName={props.customerName}
      />

      <SuiteReportBody {...props} data={liveData} />

      {/* Merged enhancements — same envelope, own titled section */}
      {Object.entries(enhancements).map(([enhancementKey, enhancementData]) => (
        <div key={enhancementKey} className="border-t-2 border-violet-300 dark:border-violet-700 pt-4">
          <h2 className="text-sm font-bold uppercase tracking-wide text-violet-700 dark:text-violet-400 mb-3">
            {TOOL_DISPLAY_NAMES[enhancementKey.split(':')[0]] || enhancementKey} — added {enhancementData.responseMetadata?.generatedAt ? new Date(enhancementData.responseMetadata.generatedAt).toLocaleDateString() : ''}
          </h2>
          <SuiteReportBody {...props} data={enhancementData} isEnhancement />
        </div>
      ))}

      {/* Suggested follow-on analyses — always last, even on a merged report */}
      {suggested.length > 0 && (
        <div id="suite-suggested-analyses" className="space-y-3 border-t-2 border-gray-200 dark:border-gray-700 pt-6">
          {suggested.map((s, i) => {
            const isCompile = COMPILE_TOOL_TYPES.has(s.toolType);
            const already = !isCompile && !!enhancements[s.toolType];
            const busy = enhancingTool === s.toolType;
            return (
              <div key={i} className="bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800 rounded-lg p-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-violet-900 dark:text-violet-200">
                    This report would benefit from additional analysis: {TOOL_DISPLAY_NAMES[s.toolType] || s.toolType}
                  </p>
                  <p className="text-sm text-violet-800 dark:text-violet-300 mt-1">{s.reason}</p>
                  <p className="text-xs text-violet-600 dark:text-violet-400 mt-1 italic">Triggered by: {s.triggeringFinding}</p>
                </div>
                <button
                  type="button"
                  disabled={already || busy || !props.reportId}
                  onClick={() => runEnhancement(s.toolType)}
                  className="flex-shrink-0 px-3 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {busy
                    ? (isCompile ? 'Compiling…' : 'Running…')
                    : already
                    ? 'Already merged'
                    : isCompile
                    ? 'Compile portfolio brief from these reports'
                    : 'Run & merge into this report'}
                </button>
              </div>
            );
          })}
          {enhanceError && <p className="text-sm text-red-600 dark:text-red-400">{enhanceError}</p>}
        </div>
      )}
    </div>
  );
};

/** Back-compat alias — the component was originally cyber-only; kept so existing imports never break. */
export const CyberSuiteReportRenderer = SuiteReportRenderer;

export default SuiteReportRenderer;
